import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, Clock } from 'lucide-react';
import Reveal from '../components/Reveal';
import ShareButtons from '../components/ShareButtons';
import NewsletterForm from '../components/NewsletterForm';
import RelatedArticles from '../components/RelatedArticles';
import ArticleBody from '../components/ArticleBody';
import ReadingProgress from '../components/ReadingProgress';
import { articles } from '../data/mock';

const categories = ['Tous', ...Array.from(new Set(articles.map((a) => a.category)))];

const ArticleCard = ({ a, i }) => (
  <Reveal key={a.slug} delay={i * 60}>
    <Link to={`/actualites/${a.slug}`} className="group block bg-[#fbf9f5] border border-black/5 hover:border-[#ff7300] transition-colors h-full" data-testid={`article-card-${a.slug}`}>
      <div className="aspect-[16/10] overflow-hidden">
        <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className="p-7">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#010c23]/50">
          <span className="text-[#ff7300]">{a.category}</span> <span>•</span> <span>{a.readTime}</span>
        </div>
        <h3 className="font-serif text-xl text-[#010c23] mt-3 leading-snug group-hover:text-[#ff7300] transition-colors">{a.title}</h3>
        <p className="text-sm text-[#010c23]/65 mt-3 leading-relaxed">{a.excerpt}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#010c23] group-hover:text-[#ff7300] transition-colors">Lire l’article <ArrowRight size={14} /></div>
      </div>
    </Link>
  </Reveal>
);

const CategoryFilter = ({ active, onChange }) => (
  <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par thème" data-testid="article-filters">
    {categories.map((c) => (
      <button key={c} type="button" onClick={() => onChange(c)} aria-pressed={active === c} data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        className={`px-4 py-2 text-xs tracking-wider uppercase font-medium border transition-colors duration-300 ${active === c ? 'bg-[#ff7300] border-[#ff7300] text-white' : 'border-black/15 text-[#010c23]/70 hover:border-[#ff7300] hover:text-[#ff7300]'}`}>
        {c}
      </button>
    ))}
  </div>
);

export const ArticlesList = () => {
  const [active, setActive] = useState('Tous');
  const visible = active === 'Tous' ? articles : articles.filter((a) => a.category === active);
  return (
    <div>
      <section className="pt-40 pb-20 bg-[#010c23] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-14 bg-[#ff7300]" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Actualités patrimoniales</span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-tight max-w-4xl">Nos analyses & <span className="italic text-[#ff7300]">décryptages</span></h1>
            <p className="mt-8 text-lg text-white/70 max-w-2xl">Articles pédagogiques pour mieux comprendre les outils patrimoniaux et les enjeux fiscaux.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
            <CategoryFilter active={active} onChange={setActive} />
            <p className="text-sm text-[#010c23]/50" data-testid="article-count">{visible.length} article{visible.length > 1 ? 's' : ''}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 no-justify">
            {visible.map((a, i) => <ArticleCard key={a.slug} a={a} i={i} />)}
          </div>
        </div>
      </section>
      <NewsletterForm source="actualites" />
    </div>
  );
};

export const ArticleDetail = () => {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const title = article?.title;
  useEffect(() => {
    if (title) document.title = `${title} | Ometis Patrimoine`;
  }, [title]);
  if (!article) return <Navigate to="/actualites" replace />;
  return (
    <div>
      <ReadingProgress />
      <section className="relative pt-40 pb-16 bg-[#010c23] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={article.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#010c23]/60 to-[#010c23]" />
        </div>
        <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12">
          <Link to="/actualites" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#ff7300] mb-8"><ArrowLeft size={14} /> Retour aux articles</Link>
          <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-white/60 mb-6">
            <span className="text-[#ff7300]">{article.category}</span>
            <span className="flex items-center gap-2"><Calendar size={12} /> {article.date}</span>
            <span className="flex items-center gap-2"><Clock size={12} /> {article.readTime}</span>
          </div>
          <h1 className="font-serif text-3xl md:text-5xl font-light leading-tight">{article.title}</h1>
          <div className="mt-8"><ShareButtons slug={article.slug} title={article.title} excerpt={article.excerpt} light /></div>
        </div>
      </section>

      <ArticleBody article={article} />

      <section className="pb-20 bg-white">
        <div className="max-w-[760px] mx-auto px-6 lg:px-12">
          <div className="pt-8 border-t border-black/10"><ShareButtons slug={article.slug} title={article.title} excerpt={article.excerpt} /></div>
        </div>
      </section>
      <RelatedArticles current={article} />
      <NewsletterForm source={`article:${article.slug}`} />
    </div>
  );
};
